/**
 * `searchMap` + helpers — the PURE matcher behind the editorial ⌘K find
 * palette (B2.13). Given the already-loaded node forest and a query string, it
 * returns the matching nodes with the breadcrumb path of each (ancestor titles,
 * root → parent) and the matched-substring span so the view can wrap it in a
 * `<mark>`. No data fetch, no React — search is purely in-memory over the nodes
 * the Map already holds (`buildTree`'s result), so it stays fast and testable.
 *
 * Issue #1043 (Wave 5). Match rule: case-insensitive SUBSTRING on the node
 * title — the simplest rule a lead reaches for ("type a word, see the tasks").
 * Results keep the forest's depth-first order (the same order the canvas /
 * outline already present), so the list reads top-down like the map. The
 * breadcrumb is the chain of ancestor titles joined by " › " (the wireframe's
 * "Publish › Lit review"); a root node has an empty breadcrumb.
 *
 * Display-only, about the WORK (titles + structure), never a person (§A9).
 */

import type { TreeNode } from "../tree/buildTree";
import { flattenTree } from "../tree/buildTree";
import type { TaskState } from "../tree/state";

/** One ⌘K result row: the node + where the match landed + its breadcrumb. */
export interface MapSearchResult {
  id: string;
  title: string;
  state: TaskState;
  /** Membership ids assigned to this node (for the row's avatar). */
  assigneeIds: string[];
  /**
   * Ancestor titles root→parent, e.g. `["Publish", "Lit review"]`. Empty for a
   * root node. Joined with " › " by the view.
   */
  breadcrumb: string[];
  /** Start index of the matched substring within `title` (lower-cased compare). */
  matchStart: number;
  /** Length of the matched substring (= the query length). */
  matchLength: number;
}

/**
 * Search the loaded forest for nodes whose title contains `query`
 * (case-insensitive substring). Returns results in the forest's depth-first
 * order. A blank / whitespace-only query returns no results (the palette shows
 * its empty hint, not the whole tree).
 */
export function searchMap(roots: readonly TreeNode[], query: string): MapSearchResult[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [];

  // Resolve each node's ancestor-title breadcrumb in one pass over the forest,
  // carrying the path down so a deep node costs O(depth), not O(depth²).
  const results: MapSearchResult[] = [];
  const walk = (node: TreeNode, ancestorTitles: string[]) => {
    const matchStart = node.title.toLowerCase().indexOf(needle);
    if (matchStart >= 0) {
      results.push({
        id: node.id,
        title: node.title,
        state: node.state,
        assigneeIds: node.assigneeIds,
        breadcrumb: ancestorTitles,
        matchStart,
        matchLength: needle.length,
      });
    }
    const nextPath = [...ancestorTitles, node.title];
    for (const child of node.children) walk(child, nextPath);
  };
  for (const root of roots) walk(root, []);
  return results;
}

/**
 * Count of nodes in the forest (the palette's "search the map…" reaches all of
 * them) — handy for the empty-query placeholder copy. Kept here so the view
 * doesn't re-walk the tree itself.
 */
export function totalNodeCount(roots: readonly TreeNode[]): number {
  return flattenTree(roots).length;
}
